import { useQuery } from "@tanstack/react-query";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { Workflow } from "~/types/api";

const fetchWorkflowBundle = async (workflowId: number): Promise<Workflow> => {
  const [workflowRes, fieldsRes, statusesRes, actionsRes] = await Promise.all([
    configurationApi.client.GET("/api/Workflow/{workflowId}", {
      params: { path: { workflowId } },
    }),
    configurationApi.client.GET("/api/workflow/{workflowId}/Field", {
      params: { path: { workflowId } },
    }),
    configurationApi.client.GET("/api/workflow/{workflowId}/Status", {
      params: { path: { workflowId } },
    }),
    configurationApi.client.GET("/api/workflow/{workflowId}/Procedure", {
      params: { path: { workflowId } },
    }),
  ]);

  if (workflowRes?.error) {
    const error = getErrorMessage(workflowRes.error, "Failed to fetch workflow details");
    throw new Error(error);
  }
  if (fieldsRes?.error) {
    const error = getErrorMessage(fieldsRes.error, "Failed to fetch workflow fields");
    throw new Error(error);
  }
  if (statusesRes?.error) {
    const error = getErrorMessage(statusesRes.error, "Failed to fetch workflow statuses");
    throw new Error(error);
  }
  const workflow = workflowRes.data;
  if (!workflow) {
    throw new Error("Workflow not found");
  }

  return {
    ...workflowRes.data,
    fields: fieldsRes.data ?? [],
    statuses: statusesRes.data ?? [],
    actions: actionsRes.data ?? [],
  };
};

export const workflowKeys = {
  byId: (workflowId: number) =>
    configurationApi.queryOptions("get", "/api/Workflow/{workflowId}", {
      params: { path: { workflowId } },
    }).queryKey,
};

export const workflowBundleQueryOptions = (workflowId: number) => ({
  queryKey: [...workflowKeys.byId(workflowId), "bundle"] as const,
  queryFn: () => fetchWorkflowBundle(workflowId),
  staleTime: 30_000, // 30 sec
});

export const useFetchWorkflowDetails = (
  workflowId: number | null,
  options?: { enabled?: boolean }
) => {
  if (workflowId == null) {
    return useQuery<Workflow, Error>({
      queryKey: ["workflow-details", "no-id"],
      queryFn: () => Promise.reject(new Error("workflowId is required to fetch workflow details")),
      enabled: false,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      placeholderData: (previousData) => previousData,
    });
  }
  return useQuery<Workflow, Error>({
    ...workflowBundleQueryOptions(workflowId!),
    enabled: !!workflowId && options?.enabled !== false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
  });
};
